import { styled } from '@linaria/react';
import { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { icons } from '../../../../assets/icons/icons';
import { getTurnBarAppearance } from './agent-turn-rail.helpers';

/** Peak bar width (px) — used to offset the flyout past the widest hover state. */
const PEAK_BAR_WIDTH = 36;
/** Gap (px) between a bar and its flyout. */
const FLYOUT_GAP = 14;

/**
 * A single navigable turn in the rail.
 */
interface TurnRailItem {
  /** Zero-based turn index; matches the `data-turn` anchor on the message. */
  index: number;
  /** The user's prompt for this turn, shown as the flyout title. */
  prompt: string;
  /** Plain-text preview of Flora's reply, shown under the prompt. */
  preview: string;
}

interface AgentTurnRailProps {
  turns: TurnRailItem[];
  /** Index of the turn currently scrolled into view. */
  activeIndex: number;
  /** Invoked with a turn index to smooth-scroll that turn into view. */
  onJump: (index: number) => void;
}

/** Viewport coordinates for the hovered turn's flyout. */
interface FlyoutAnchor {
  index: number;
  top: number;
  left: number;
}

/**
 * Vertical turn-navigation rail pinned to the left edge of the Flora chat.
 *
 * Renders one condensed grey bar per conversation turn. Hovering a bar swells
 * the rail into a waveform centered on it and reveals a flyout previewing that
 * turn's prompt and Flora's reply; the turn currently scrolled into view keeps
 * a subtle highlight. Clicking a bar jumps to that turn.
 *
 * The bar column is height-bounded and scrolls internally, so every turn stays
 * reachable on long threads. The flyout is rendered in a portal with fixed
 * positioning so the column's overflow clipping never hides it.
 *
 * @param {AgentTurnRailProps} props - The component props
 * @param {TurnRailItem[]} props.turns - One entry per conversation turn
 * @param {number} props.activeIndex - Turn currently scrolled into view
 * @param {(index: number) => void} props.onJump - Scrolls the given turn into view
 * @returns {JSX.Element} The rendered navigation rail
 */
export function AgentTurnRail({ turns, activeIndex, onJump }: AgentTurnRailProps) {
  // Mouse and keyboard focus are tracked independently so that leaving a
  // moused-over bar can't dismiss a flyout that a *different* bar still holds
  // via focus (and vice versa). Focus wins when both are active.
  const [mouseAnchor, setMouseAnchor] = useState<FlyoutAnchor | null>(null);
  const [focusAnchor, setFocusAnchor] = useState<FlyoutAnchor | null>(null);
  const anchor = focusAnchor ?? mouseAnchor;
  const hoveredIndex = anchor?.index ?? -1;

  const computeAnchor = useCallback((index: number, row: HTMLElement): FlyoutAnchor => {
    const rect = row.getBoundingClientRect();
    return {
      index,
      top: rect.top + rect.height / 2,
      left: rect.left + PEAK_BAR_WIDTH + FLYOUT_GAP,
    };
  }, []);

  const clearIfCurrent = useCallback(
    (setter: typeof setMouseAnchor, index: number) => setter((prev) => (prev?.index === index ? null : prev)),
    [],
  );

  const hoveredTurn = hoveredIndex >= 0 ? turns.find((turn) => turn.index === hoveredIndex) : undefined;

  return (
    <RailContainer aria-label="Conversation turns">
      <RailBars>
        {turns.map((turn) => {
          const { width, background } = getTurnBarAppearance(turn.index, hoveredIndex, activeIndex);
          return (
            <BarRow
              key={turn.index}
              type="button"
              onMouseEnter={(e) => setMouseAnchor(computeAnchor(turn.index, e.currentTarget))}
              onMouseLeave={() => clearIfCurrent(setMouseAnchor, turn.index)}
              onFocus={(e) => setFocusAnchor(computeAnchor(turn.index, e.currentTarget))}
              onBlur={() => clearIfCurrent(setFocusAnchor, turn.index)}
              onClick={() => onJump(turn.index)}
              aria-label={`Jump to turn ${turn.index + 1}: ${turn.prompt}`}
            >
              <Bar style={{ width, background }} />
            </BarRow>
          );
        })}
      </RailBars>

      {hoveredTurn &&
        anchor &&
        createPortal(
          <Flyout style={{ top: anchor.top, left: anchor.left }}>
            <FlyoutLabel>{hoveredTurn.prompt}</FlyoutLabel>
            {hoveredTurn.preview && (
              <FlyoutPreviewRow>
                <FlyoutGlyph src={icons.iconFlora} alt="Flora" />
                <FlyoutPreview>{hoveredTurn.preview}</FlyoutPreview>
              </FlyoutPreviewRow>
            )}
          </Flyout>,
          document.body,
        )}
    </RailContainer>
  );
}

const RailContainer = styled.div`
  position: absolute;
  left: 6px;
  top: 0;
  bottom: 0;
  z-index: 12;
  display: flex;
  align-items: center;
  padding-left: 6px;
  pointer-events: none;
`;

const RailBars = styled.div`
  display: flex;
  flex-direction: column;
  gap: 5px;
  align-items: flex-start;
  max-height: 100%;
  overflow-y: auto;
  /* Re-enable pointer events so the column scrolls on long threads; the parent
     stays pointer-events:none so the rail never blocks the message content. */
  pointer-events: auto;

  /* Keep the rail reading as a clean waveform — scrollable but chromeless. */
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const BarRow = styled.button`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 4px 0;
  margin: 0;
  border: none;
  background: none;
  cursor: pointer;
  pointer-events: auto;
`;

const Bar = styled.span`
  height: 3px;
  border-radius: 2px;
  transition: width 0.32s cubic-bezier(0.22, 1, 0.36, 1), background 0.32s ease;
`;

const Flyout = styled.div`
  position: fixed;
  z-index: 1000;
  transform: translateY(-50%);
  width: 264px;
  background: var(--base-white);
  border: 1px solid var(--sky-light);
  border-radius: 12px;
  box-shadow: var(--box-shadow-medium-hard);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  text-align: left;
  pointer-events: none;
  animation: fl-flyout-in 0.16s ease both;

  @keyframes fl-flyout-in {
    from {
      opacity: 0;
      transform: translate(-6px, -50%);
    }
    to {
      opacity: 1;
      transform: translate(0, -50%);
    }
  }
`;

const FlyoutLabel = styled.span`
  font: 600 13px/1.4 'Figtree', sans-serif;
  color: var(--ink-darkest);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const FlyoutPreviewRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 7px;
`;

const FlyoutGlyph = styled.img`
  flex-shrink: 0;
  width: 15px;
  height: 15px;
  margin-top: 1px;
`;

const FlyoutPreview = styled.span`
  font: 400 12px/1.5 'Figtree', sans-serif;
  color: var(--ink-light);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export type { TurnRailItem };
